import React, { useEffect, useState } from 'react';
import { User, Check } from 'lucide-react';
import PageHero from '../components/PageHero';
import apiClient from '../utils/apiClient';
import { getTaskTitle } from '../utils/statsUtils';

const WORKFLOW_STEPS = [
  { order: 1, name: 'Strategy Planning', desc: 'Define goals, audience, style, and format.', agent: 'Content Strategist' },
  { order: 2, name: 'Research & Gathering', desc: 'Compile sources and data relevant to the topic.', agent: 'Research Writer' },
  { order: 3, name: 'Outline Development', desc: 'Create logical structure reflecting chosen format.', agent: 'Research Writer' },
  { order: 4, name: 'First Draft', desc: 'Write initial draft based on research and outline.', agent: 'Research Writer' },
  { order: 5, name: 'Fact-Checking', desc: 'Verify all facts, data, and cited sources.', agent: 'Fact Checker' },
  { order: 6, name: 'Multi-Layered Editing', desc: 'Edit for clarity, flow, tone, SEO, and brand voice.', agent: 'Managing Editor' },
  { order: 7, name: 'Final Approval', desc: 'Strategy alignment check and final sign-off.', agent: 'Content Strategist' },
  { order: 8, name: 'Publication', desc: 'Publish and distribute across target channels.', agent: 'SEO Specialist' },
  { order: 9, name: 'Post-Pub Monitoring', desc: 'Track performance and iterate based on analytics.', agent: 'Content Strategist' },
];

function ProjectProgressDots({ completedCount }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {Array.from({ length: 9 }, (_, i) => {
        const isCompleted = i < completedCount;
        const isCurrent = i === completedCount;
        return (
          <div key={i} style={{
            width: 24, height: 8, borderRadius: 4,
            background: isCompleted ? '#1E3A8A' : isCurrent ? '#F97316' : 'rgba(255,255,255,0.1)',
          }} />
        );
      })}
    </div>
  );
}

export default function TheBlueprint() {
  const [instances, setInstances] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    apiClient.get('/workflow-instances/')
      .then(r => {
        const list = r.data?.results || r.data || [];
        setInstances(list);
        const active = list.find(i => i.status === 'in_progress');
        if (active) setSelectedId(active.id);
      })
      .catch(() => {});
  }, []);

  const selectedInstance = instances.find(i => i.id === selectedId);
  const completedStepNums = selectedInstance
    ? Object.keys(selectedInstance.completed_steps || {}).map(Number)
    : [];
  const currentStepNum = completedStepNums.length > 0 ? Math.max(...completedStepNums) + 1 : 1;

  const activeProjects = instances.filter(i => i.status === 'in_progress').slice(0, 4);

  const page = {
    maxWidth: 900,
    margin: '0 auto',
    padding: isMobile ? '20px 16px 24px' : '24px',
    fontFamily: 'Inter, sans-serif',
    width: '100%',
    minWidth: 0,
  };
  const card = {
    background: '#1A2236',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: 20,
  };

  return (
    <div style={page}>
      <PageHero
        badge="PIPELINE"
        title="The Blueprint"
        subtitle="Visualize the 9-step workflow pipeline. Every piece of content flows through this structured process."
      />

      {/* Active Projects */}
      <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 20, color: '#F1F5F9', marginBottom: 16 }}>
        Active Projects
      </h2>
      {activeProjects.length === 0 ? (
        <div style={{ ...card, color: '#6B7280', textAlign: 'center', padding: 24, marginBottom: 32 }}>
          No active projects.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 40 }}>
          {activeProjects.map(inst => {
            const done = Object.keys(inst.completed_steps || {}).length;
            return (
              <div
                key={inst.id}
                style={{
                  ...card, cursor: 'pointer',
                  outline: selectedId === inst.id ? '2px solid #F97316' : 'none',
                }}
                onClick={() => setSelectedId(inst.id)}
              >
                <div style={{ fontWeight: 700, fontSize: 15, color: '#F1F5F9', marginBottom: 4, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                  {getTaskTitle(inst)}
                </div>
                <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 12 }}>
                  Step {done} of 9
                </div>
                <ProjectProgressDots completedCount={done} />
              </div>
            );
          })}
        </div>
      )}

      {/* The 9-Step Workflow */}
      <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 20, color: '#F1F5F9', marginBottom: 24 }}>
        The 9-Step Workflow
      </h2>
      <div style={{ ...card, padding: isMobile ? '20px 16px' : '24px 28px', overflow: 'hidden' }}>
        {WORKFLOW_STEPS.map((step, idx) => {
          const isCompleted = completedStepNums.includes(step.order);
          const isCurrent = step.order === currentStepNum && !!selectedInstance;
          const isLast = idx === WORKFLOW_STEPS.length - 1;

          return (
            <div key={step.order} style={{ display: 'flex', gap: isMobile ? 12 : 16, position: 'relative', minWidth: 0 }}>
              {/* Connector line */}
              {!isLast && (
                <div style={{
                  position: 'absolute',
                  left: 20, top: 44, bottom: -8,
                  width: 2,
                  background: isCompleted ? '#1E3A8A' : 'rgba(255,255,255,0.08)',
                  zIndex: 0,
                }} />
              )}

              {/* Step indicator */}
              <div style={{ flexShrink: 0, zIndex: 1 }}>
                {isCompleted ? (
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: '#1E3A8A',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Check size={18} color="white" strokeWidth={2.5} />
                  </div>
                ) : isCurrent ? (
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: '#F97316',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 700, fontSize: 15,
                  }}>{step.order}</div>
                ) : (
                  <div style={{
                    width: 40, height: 40, borderRadius: 20,
                    border: '2px solid rgba(255,255,255,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#6B7280', fontWeight: 600, fontSize: 15,
                    background: 'rgba(255,255,255,0.04)',
                  }}>{step.order}</div>
                )}
              </div>

              {/* Content */}
              <div style={{
                flex: 1,
                display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'flex-start',
                paddingBottom: isLast ? 0 : 28,
                paddingTop: 8,
                minWidth: 0,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontWeight: 700, fontSize: 15,
                    color: isCurrent ? '#F97316' : isCompleted ? '#F1F5F9' : '#9CA3AF',
                    marginBottom: 2,
                    overflowWrap: 'anywhere',
                  }}>{step.name}</div>
                  <div style={{ fontSize: 13, color: '#6B7280', overflowWrap: 'anywhere' }}>{step.desc}</div>
                  {isCurrent && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#F97316' }} />
                      <span style={{ fontSize: 12, color: '#F97316', fontWeight: 500 }}>
                        Currently in progress — {step.agent} is editing
                      </span>
                    </div>
                  )}
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  color: '#6B7280', fontSize: 12, marginTop: isMobile ? 8 : 2,
                  whiteSpace: isMobile ? 'normal' : 'nowrap', marginLeft: isMobile ? 0 : 16,
                  overflowWrap: 'anywhere',
                }}>
                  <User size={13} />
                  {step.agent}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
